import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Boat, ApiError } from '../../lib/api.ts';

export function MyBoats() {
  const qc = useQueryClient();
  const boats = useQuery({ queryKey: ['my-boats'], queryFn: () => api.myBoats() });
  const [editing, setEditing] = useState<Boat | null>(null);
  const [showNew, setShowNew] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold">Mine båter</h1>
        <button
          className="bg-sky-600 hover:bg-sky-500 px-3 py-2 rounded text-sm font-semibold"
          onClick={() => {
            setShowNew(true);
            setEditing(null);
          }}
        >
          + Legg til båt
        </button>
      </div>

      {(showNew || editing) && (
        <BoatForm
          initial={editing ?? undefined}
          onClose={() => {
            setShowNew(false);
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['my-boats'] });
          }}
        />
      )}

      {boats.isLoading ? (
        <p className="text-slate-500 text-sm">Laster…</p>
      ) : boats.data && boats.data.length > 0 ? (
        <ul className="divide-y divide-slate-800 border border-slate-800 rounded-xl overflow-hidden">
          {boats.data.map((b) => (
            <li key={b.id} className="p-3 bg-slate-900/30">
              <div className="flex items-center gap-3">
                <div className="font-mono text-xs w-16 text-slate-400">{b.sailNumber}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate flex items-center gap-2">
                    {b.boatName}
                    {b.className && (
                      <span className="text-xs bg-slate-700/40 text-slate-300 px-2 py-0.5 rounded">
                        {b.className}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 truncate">
                    Skipper: {b.skipperName}
                    {b.crew && ` • Mannskap: ${b.crew}`}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setEditing(b);
                    setShowNew(false);
                  }}
                  className="text-xs text-sky-400"
                >
                  Rediger
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-center text-slate-500 text-sm p-6 border border-slate-800 rounded-xl">
          Du har ingen båter ennå. Legg til den første.
        </div>
      )}
    </div>
  );
}

function BoatForm({ initial, onClose }: { initial?: Boat; onClose: () => void }) {
  const qc = useQueryClient();
  const classes = useQuery({ queryKey: ['classes', false], queryFn: () => api.listClasses(false) });

  const [form, setForm] = useState({
    sailNumber: initial?.sailNumber ?? 'GJ',
    boatName: initial?.boatName ?? '',
    skipperName: initial?.skipperName ?? '',
    crew: initial?.crew ?? '',
    classId: initial?.classId ?? '',
    notes: initial?.notes ?? '',
  });
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        sailNumber: form.sailNumber,
        boatName: form.boatName,
        skipperName: form.skipperName,
        crew: form.crew || null,
        classId: form.classId || null,
        notes: form.notes || null,
      };
      if (initial) return api.updateMyBoat(initial.id, payload);
      return api.createMyBoat(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-boats'] });
      onClose();
    },
    onError: (e: ApiError) => setErr(e.message),
  });

  const archive = useMutation({
    mutationFn: async () => initial && api.archiveMyBoat(initial.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-boats'] });
      onClose();
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      className="mb-4 bg-slate-900/60 border border-slate-800 rounded-xl p-4 space-y-3"
    >
      <h2 className="font-semibold">{initial ? 'Rediger båt' : 'Ny båt'}</h2>
      {err && (
        <div className="text-red-300 bg-red-900/30 border border-red-800 rounded p-2 text-sm">
          {err}
        </div>
      )}

      <div>
        <label className="block text-xs text-slate-400 mb-1">Seilnummer (GJxxxx)</label>
        <input
          value={form.sailNumber}
          onChange={(e) => setForm({ ...form, sailNumber: e.target.value.toUpperCase() })}
          required
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg font-mono"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Båtnavn</label>
        <input
          value={form.boatName}
          onChange={(e) => setForm({ ...form, boatName: e.target.value })}
          required
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Skipper</label>
        <input
          value={form.skipperName}
          onChange={(e) => setForm({ ...form, skipperName: e.target.value })}
          required
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Mannskap (fri tekst)</label>
        <input
          value={form.crew ?? ''}
          onChange={(e) => setForm({ ...form, crew: e.target.value })}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          placeholder="Ole, Kari, Per"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Klasse</label>
        <select
          value={form.classId ?? ''}
          onChange={(e) => setForm({ ...form, classId: e.target.value })}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        >
          <option value="">— Ingen klasse —</option>
          {(classes.data ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <p className="text-xs text-slate-500 mt-1">
          Klasse må stemme overens med regattaens tillatte klasser for å kunne melde på.
        </p>
      </div>

      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          disabled={save.isPending}
          className="flex-1 bg-sky-600 hover:bg-sky-500 py-3 rounded font-semibold disabled:opacity-60"
        >
          {save.isPending ? 'Lagrer…' : 'Lagre'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded font-semibold"
        >
          Avbryt
        </button>
      </div>
      {initial && (
        <button
          type="button"
          onClick={() => {
            if (confirm(`Arkivere ${initial.boatName}? Båten kan ikke meldes på nye regattaer.`)) {
              archive.mutate();
            }
          }}
          className="w-full text-xs text-red-400 py-2"
        >
          Arkiver båt
        </button>
      )}
    </form>
  );
}
